import React, { useCallback, useEffect } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { css } from '@betterment/samba-styled-system/css';

import { ProcessCapitalizeWindowEventDocument } from './graphql/mutation.processCapitalizeWindowEvent.generated';

import { GraphQLProvider } from '../RetailApp/graphql';
import { IdempotencyProviderWithFeatureRegistry } from '../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';

export interface CapitalizeWebviewContentProps {
  webviewUrl: string;
  internalRolloverRedirectPath: string;
}

export const CapitalizeWebviewContent: React.FC<CapitalizeWebviewContentProps> = ({
  webviewUrl,
  internalRolloverRedirectPath,
}) => {
  const [processWindowEvent] = useGraphQLMutation(ProcessCapitalizeWindowEventDocument);

  const handleEvent = useCallback(
    async (event: MessageEvent<EventData>) => {
      const { data } = event;

      if ('onPartnerInternalRolloverRedirect' in data) {
        window.location.href = internalRolloverRedirectPath;
      }

      if ('onError' in data) {
        window.console.error('Capitalize webview error', data.onError);
      }

      try {
        const { data: mutationResult } = await processWindowEvent({
          variables: {
            input: {
              eventPayload: data,
            },
          },
        });

        if (mutationResult?.processCapitalizeWindowEvent.displayableErrors[0]) {
          window.console.error(
            'Capitalize window event error',
            mutationResult.processCapitalizeWindowEvent.displayableErrors[0].message
          );
        }
      } catch (error) {
        window.console.error('Error processing window event', error);
      }
    },
    [internalRolloverRedirectPath, processWindowEvent]
  );

  useEffect(() => {
    window.addEventListener('message', handleEvent);
    return () => window.removeEventListener('message', handleEvent);
  }, [handleEvent]);

  return (
    <iframe
      className={css({
        width: '100%',
        height: '500px',
        border: 'none',
      })}
      src={webviewUrl}
      title="Capitalize rollover webview"
    />
  );
};

type TransactionId = string;
type ErrorMessage = string;
type EventData =
  | { onCapitalize401kStepComplete: TransactionId }
  | { onCapitalizeExit: true }
  | { onCapitalizeRolloverCanceled: TransactionId }
  | { onCapitalizeRolloverCreated: TransactionId }
  | { onCapitalizeRolloverSubmitted: true }
  | { onCapitalizeRolloverSubmittedV2: TransactionId }
  | { onCreateRothDestinationNeeded: TransactionId }
  | { onCreateRothIra: TransactionId }
  | { onCreateTradDestinationNeeded: TransactionId }
  | { onCreateTradIra: TransactionId }
  | { onError: ErrorMessage }
  | { onPartnerInternalRolloverRedirect: true };

const CapitalizeWebviewContentWithIdempotency: React.FC<CapitalizeWebviewContentProps> = props => {
  return (
    <GraphQLProvider>
      <IdempotencyProviderWithFeatureRegistry featureName="capitalizeRollovers">
        <CapitalizeWebviewContent {...props} />
      </IdempotencyProviderWithFeatureRegistry>
    </GraphQLProvider>
  );
};

export default CapitalizeWebviewContentWithIdempotency;
